import React from 'react';
import { Link } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import MainLayout from '@/pages/layouts/MainLayout';
import { MessageSquare, Clock, ArrowRight, Home } from 'lucide-react';

const ConversationsIndex = ({ conversations }) => {
  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* Hero section */}
      <div className="bg-primary/10 py-8 mb-8">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Messages</h1>
              <p className="text-gray-600 mt-2">Stay in touch with assisted living communities</p>
            </div>
            <Button asChild variant="outline" className="gap-2">
              <Link href="/">
                <Home className="h-4 w-4" />
                Browse Communities
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4">
        {conversations.length === 0 ? (
          <Card className="shadow-md border-0 overflow-hidden pt-0">
            <CardContent className="p-12">
              <div className="text-center space-y-4">
                <div className="mx-auto bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">No conversations yet</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Start a conversation with an assisted living community to see it here.
                </p>
                <Button asChild className="mt-4">
                  <Link href="/">Browse Communities</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {conversations.map(conversation => (
              <Link key={conversation.id} href={`/conversations/${conversation.id}`} className="block">
                <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-0 shadow-md overflow-hidden h-full pt-0">
                  <div className="relative">
                    {/* Facility image */}
                    <div className="h-36 bg-gray-200 overflow-hidden">
                      <img
                        src={conversation.facility.image_url || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"}
                        alt={conversation.facility.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Unread badge */}
                    {conversation.unread_count > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute top-3 right-3 px-2 py-1 text-xs font-bold"
                      >
                        {conversation.unread_count} new
                      </Badge>
                    )}
                  </div>

                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{conversation.facility.name}</CardTitle>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          {conversation.last_message ? (
                            <span>
                              {formatDistanceToNow(new Date(conversation.last_message.created_at), { addSuffix: true })}
                            </span>
                          ) : (
                            <span>No activity</span>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant={conversation.status === 'active' ? 'outline' : 'secondary'}
                        className="text-xs"
                      >
                        {conversation.status === 'active' ? 'Active' : 'Archived'}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="pb-3">
                    <div className="border-l-4 border-primary/20 pl-3 py-1">
                      {conversation.last_message ? (
                        <p className="line-clamp-2 text-sm text-gray-600">
                          {conversation.last_message.system_message ? (
                            <span className="italic">{conversation.last_message.content}</span>
                          ) : (
                            conversation.last_message.content
                          )}
                        </p>
                      ) : (
                        <p className="text-sm italic text-muted-foreground">No messages yet</p>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0 pb-4">
                    <Button variant="ghost" size="sm" className="ml-auto gap-1 text-primary hover:text-primary/80">
                      View Conversation
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </CardFooter>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ConversationsIndex.layout = page => <MainLayout children={page} title="My Conversations" />;

export default ConversationsIndex;
